import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  createCheckoutSession,
  getSubscriptionStatus,
} from "../api";

export default function ChoosePlan() {
  const navigate = useNavigate();

  const [loadingPlan, setLoadingPlan] = useState("");
  const [error, setError] = useState("");
  const [activating, setActivating] = useState(false);
  const [trialEligible, setTrialEligible] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkoutStatus = params.get("checkout");

    let cancelled = false;

    const saveCompanyAndContinue = (result) => {
      let company = {};

      try {
        company = JSON.parse(
          localStorage.getItem("company") || "{}"
        );
      } catch {
        company = {};
      }

      localStorage.setItem(
        "company",
        JSON.stringify({
          ...company,
          id: result.company_id,
          subscription_status: result.subscription_status,
          subscription_plan: result.subscription_plan,
        })
      );

      navigate("/dashboard", { replace: true });
    };

    const checkExistingSubscription = async () => {
      try {
        const result = await getSubscriptionStatus();

        if (!cancelled) setTrialEligible(Boolean(result?.trial_eligible));
        if (!cancelled && result?.is_active) {
          saveCompanyAndContinue(result);
        }
      } catch (err) {
        if (!cancelled) {
          setError("Unable to check your subscription. Please refresh to try again.");
        }
      }
    };

    const refreshSubscriptionAfterCheckout = async () => {
      setActivating(true);
      setError("");

      for (let attempt = 0; attempt < 15; attempt += 1) {
        if (cancelled) {
          return;
        }

        try {
          const result = await getSubscriptionStatus();

          if (result?.is_active) {
            saveCompanyAndContinue(result);
            return;
          }
        } catch (err) {
          console.error("Subscription status check failed");
        }

        await new Promise(
          (resolve) => setTimeout(resolve, 2000)
        );
      }

      if (!cancelled) {
        setActivating(false);
        setError(
          "Checkout completed, but account activation is still processing. Please refresh this page in a moment."
        );
      }
    };

    if (checkoutStatus === "cancelled") {
      setError(
        "Checkout was cancelled. You can choose a plan when you're ready."
      );

      checkExistingSubscription();
    } else if (checkoutStatus === "success") {
      refreshSubscriptionAfterCheckout();
    } else {
      checkExistingSubscription();
    }

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const handleChoosePlan = async (plan) => {
    try {
      setError("");
      setLoadingPlan(plan);

      const result = await createCheckoutSession(plan);

      if (!result?.checkout_url) {
        throw new Error("Checkout URL was not returned.");
      }

      window.location.href = result.checkout_url;
    } catch (err) {
      setError(
        err?.message ||
          "Unable to start checkout. Please try again."
      );
      setLoadingPlan("");
    }
  };

  const plans = [
    {
      key: "starter",
      price: 99,
      name: "Starter",
      description: "Core tools to manage your credit repair clients.",
    },
    {
      key: "professional",
      price: 199,
      name: "Professional",
      description:
        "Advanced features including employees, analytics, and case files.",
    },
    {
      key: "white_label",
      price: 399,
      name: "White Label",
      description:
        "Professional features plus custom business branding.",
    },
  ];

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f8fafc",
        padding: "48px 20px",
      }}
    >
      <div
        style={{
          maxWidth: "1100px",
          margin: "0 auto",
        }}
      >
        <div
          style={{
            textAlign: "center",
            marginBottom: "40px",
          }}
        >
          <h1
            style={{
              fontSize: "clamp(38px, 6vw, 64px)",
              marginBottom: "12px",
            }}
          >
            Choose Your Plan
          </h1>

          <p
            style={{
              fontSize: "18px",
              color: "#475569",
            }}
          >
            {trialEligible === true
              ? "Start your 7-day free trial. A payment method is required. Your selected plan bills monthly after the trial unless you cancel in Settings > Billing before it ends."
              : trialEligible === false
                ? "Your trial has already been used. Choose a plan to subscribe; monthly billing starts at checkout."
                : "Checking trial eligibility..."}
          </p>
        </div>

        <p style={{ textAlign: "center" }}><a href="/billing">Manage billing or cancel an existing subscription</a></p>
        {activating && (
          <div
            style={{
              maxWidth: "700px",
              margin: "0 auto 24px",
              padding: "16px",
              border: "1px solid #d4af37",
              borderRadius: "10px",
              textAlign: "center",
              fontWeight: "700",
              background: "#fffbeb",
            }}
          >
            Checkout completed. Activating your account...
          </div>
        )}

        {error && (
          <div
            style={{
              maxWidth: "700px",
              margin: "0 auto 24px",
              padding: "14px",
              border: "1px solid #dc2626",
              borderRadius: "10px",
              color: "#991b1b",
              background: "#fef2f2",
            }}
          >
            {error}
          </div>
        )}

        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fit, minmax(260px, 1fr))",
            gap: "24px",
          }}
        >
          {plans.map((plan) => (
            <div
              key={plan.key}
              style={{
                background: "#ffffff",
                border: "1px solid #e2e8f0",
                borderRadius: "18px",
                padding: "28px",
                boxShadow: "0 10px 25px rgba(0,0,0,0.06)",
              }}
            >
              <h2
                style={{
                  fontSize: "28px",
                  marginBottom: "12px",
                }}
              >
                {plan.name}
              </h2>
              <p style={{ fontWeight: 800 }}>{trialEligible ? "$0 for 7 days, then " : ""}${plan.price}/month</p>

              <p
                style={{
                  color: "#64748b",
                  lineHeight: 1.6,
                  minHeight: "78px",
                }}
              >
                {plan.description}
              </p>

              <button
                onClick={() => handleChoosePlan(plan.key)}
                disabled={Boolean(loadingPlan) || trialEligible === null}
                style={{
                  width: "100%",
                  marginTop: "24px",
                  padding: "14px 18px",
                  borderRadius: "12px",
                  border: "none",
                  cursor: loadingPlan ? "not-allowed" : "pointer",
                  fontSize: "16px",
                  fontWeight: "700",
                  background: "#111827",
                  color: "#ffffff",
                  opacity: loadingPlan ? 0.7 : 1,
                }}
              >
                {loadingPlan === plan.key
                  ? "Opening Checkout..."
                  : trialEligible ? `Start 7-Day Trial: ${plan.name}` : `Subscribe: ${plan.name}`}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
